import React, { useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import { colorFor } from "./plannerConstants";

/**
 * Left rail of class templates. Cards drag onto a day, or are selected and
 * then a day is clicked.
 */
export default function TemplateRail({
  templates,
  classes,
  placingId,
  canEdit,
  open,
  onClose,
  onSelectPlacing,
  onDragStart,
  onDragEnd,
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const categories = useMemo(
    () => [...new Set(templates.map((t) => t.category).filter(Boolean))],
    [templates],
  );

  const scheduledCount = (tpl) =>
    classes.filter((c) => String(c.submissionId) === String(tpl.id)).length;

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates.filter(
      (t) =>
        (category === "All" || t.category === category) &&
        (!q ||
          `${t.title} ${t.courseNumber || ""} ${t.category || ""}`
            .toLowerCase()
            .includes(q)),
    );
  }, [templates, query, category]);

  return (
    <aside className={`sp-tp${open ? " open" : ""}`} aria-label="Course templates">
      <div className="sp-tp-h">
        <div>
          <h2>Course templates</h2>
          <p>
            {canEdit
              ? "Drag onto a start day, or select one and click a day."
              : "Published semesters are read-only."}
          </p>
        </div>
        <button
          type="button"
          className="sp-pib sp-tp-x"
          onClick={onClose}
          aria-label="Close templates"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      </div>
      <label className="sp-srch">
        <SearchIcon sx={{ fontSize: 15 }} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or code"
          aria-label="Search templates"
        />
      </label>
      <div className="sp-chips">
        <button
          type="button"
          className={category === "All" ? "on" : ""}
          onClick={() => setCategory("All")}
        >
          All <em>{templates.length}</em>
        </button>
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            className={category === c ? "on" : ""}
            onClick={() => setCategory(c)}
          >
            <i style={{ background: colorFor(c) }} />
            {c}
          </button>
        ))}
      </div>
      <div className="sp-tp-l">
        {list.map((t) => {
          const n = scheduledCount(t);
          return (
            <div
              key={t.id}
              className={`sp-tc${placingId === t.id ? " on" : ""}${canEdit ? "" : " ro"}`}
              style={{ "--c": colorFor(t.category) }}
              data-testid={`planner-template-${t.id}`}
              role="button"
              tabIndex={canEdit ? 0 : -1}
              aria-pressed={placingId === t.id}
              draggable={canEdit}
              onDragStart={(e) => canEdit && onDragStart(e, t)}
              onDragEnd={onDragEnd}
              onClick={() => canEdit && onSelectPlacing(placingId === t.id ? null : t.id)}
              onKeyDown={(e) =>
                canEdit && (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelectPlacing(placingId === t.id ? null : t.id))
              }
            >
              <span className="sp-tc-g">
                <DragIndicatorIcon sx={{ fontSize: 16 }} />
              </span>
              <div className="sp-tc-m">
                <div className="sp-tc-t">
                  <i />
                  <b>{t.title}</b>
                </div>
                <span>
                  {t.courseNumber && <span className="sp-mo">{t.courseNumber} · </span>}
                  {t.totalHours || 0} hrs · {t.capacity || t.availableSeats || 24} seats
                </span>
              </div>
              <span className={`sp-tc-n${n ? "" : " z"}`}>{n ? `${n}×` : "None"}</span>
            </div>
          );
        })}
        {!list.length && <p className="sp-emp">No templates match.</p>}
      </div>
    </aside>
  );
}
